import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Calendar, MapPin, Loader2, Trash2, Pencil, ArrowLeft, ArrowRight,
  CheckSquare, Square, Save, Building2, Users, BarChart3, ShieldCheck, FileText, XCircle, LayoutTemplate, FileSignature, Ban, Activity,
} from 'lucide-react';
import SeatClearancePanel from '../components/SeatClearancePanel';
import SeatAllotmentPanel from '../components/SeatAllotmentPanel';
import SeatCancellationPanel from '../components/SeatCancellationPanel';
import SeatTemplatesPanel from '../components/SeatTemplatesPanel';
import SeatTermsPanel from '../components/SeatTermsPanel';
import SeatRestrictionsPanel from '../components/SeatRestrictionsPanel';
import SeatHofSessionsPanel from '../components/SeatHofSessionsPanel';
import { SeatReportsPanel, SeatAuditPanel } from '../components/SeatReportsPanel';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

// ============== STATS CARDS ==============
const StatsDrilldownSheet = ({ open, kind, label, eventId, onClose }) => {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open || !kind) return;
    setLoading(true);
    setQ('');
    api.get(`/seat/stats/drilldown?kind=${kind}${eventId ? `&event_id=${eventId}` : ''}&limit=2000`)
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [open, kind, eventId]);

  const filtered = q.trim()
    ? rows.filter(r =>
        JSON.stringify(r).toLowerCase().includes(q.trim().toLowerCase()),
      )
    : rows;

  const COLUMNS = {
    events: [
      ['name', 'Name'], ['year', 'Year'], ['category', 'Category'],
      ['status', 'Status'], ['booking_start', 'Booking Opens'], ['booking_end', 'Booking Closes'],
    ],
    floors: [
      ['name', 'Floor'], ['event_name', 'Event'], ['event_year', 'Year'],
      ['seats', 'Seats'], ['booked', 'Booked'], ['available', 'Available'],
      ['hof_seat_limit', 'HOF Limit'],
    ],
    total_seats: [
      ['floor_name', 'Floor'], ['seat_no', 'Seat #'], ['row', 'Row'], ['col', 'Col'], ['booked', 'Booked?'],
    ],
    booked: [
      ['floor_name', 'Floor'], ['seat_no', 'Seat #'], ['member_name', 'Member'], ['its_id', 'ITS'],
      ['hof_name', 'HOF'], ['status', 'Status'], ['booked_by_name', 'Booked By'], ['booked_at', 'When'], ['pass_id', 'Pass ID'],
    ],
    available: [
      ['floor_name', 'Floor'], ['seat_no', 'Seat #'], ['row', 'Row'], ['col', 'Col'],
    ],
    hof_cleared: [
      ['hof_name', 'HOF'], ['hof_its_id', 'ITS'], ['mobile', 'Mobile'], ['mohalla', 'Mohalla'], ['sector', 'Sector'],
      ['approved_by_name', 'Approved By'], ['approved_at', 'When'],
    ],
    hof_pending: [
      ['hof_name', 'HOF'], ['hof_its_id', 'ITS'], ['mobile', 'Mobile'], ['mohalla', 'Mohalla'], ['sector', 'Sector'],
    ],
  };
  const cols = COLUMNS[kind] || [];

  const fmtCell = (k, v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (k === 'booked_at' || k === 'approved_at' || k === 'booking_start' || k === 'booking_end') {
      try { return new Date(v).toLocaleString(); } catch { return String(v); }
    }
    return String(v);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto" data-testid="stats-drilldown-sheet">
        <SheetHeader>
          <SheetTitle>{label || kind}</SheetTitle>
          <SheetDescription>
            {loading ? 'Loading…' : `${filtered.length} of ${rows.length} record${rows.length === 1 ? '' : 's'}`}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-3 mb-2">
          <Input
            placeholder="Filter rows…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="stats-drilldown-search"
          />
        </div>
        {loading ? (
          <div className="py-12 text-center text-slate-500"><Loader2 className="w-6 h-6 inline animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-sm">No records.</div>
        ) : (
          <div className="overflow-auto border rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  {cols.map(([k, hdr]) => (
                    <th key={k} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">{hdr}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((r, i) => (
                  <tr key={i} className="hover:bg-emerald-50/40">
                    {cols.map(([k]) => (
                      <td key={k} className="px-2 py-1.5 align-top">{fmtCell(k, r[k])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const StatsCards = ({ stats, onCardClick }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="seat-stats">
    {[
      { key: 'events',       label: 'Events',       val: stats.total_events,            icon: Calendar,    color: 'text-emerald-700 bg-emerald-100' },
      { key: 'floors',       label: 'Floors',       val: stats.total_floors,            icon: Building2,   color: 'text-blue-700 bg-blue-100' },
      { key: 'total_seats',  label: 'Total Seats',  val: stats.total_seats,             icon: MapPin,      color: 'text-indigo-700 bg-indigo-100' },
      { key: 'booked',       label: 'Booked',       val: stats.booked_seats,            icon: Users,       color: 'text-red-700 bg-red-100' },
      { key: 'available',    label: 'Available',    val: stats.available_seats,         icon: CheckSquare, color: 'text-green-700 bg-green-100' },
      { key: 'hof_cleared',  label: 'HOF Cleared',  val: stats.hof_clearance_approved,  icon: ShieldCheck, color: 'text-emerald-700 bg-emerald-100' },
      { key: 'hof_pending',  label: 'HOF Pending',  val: stats.hof_clearance_pending,   icon: ShieldCheck, color: 'text-amber-700 bg-amber-100' },
    ].map((s) => (
      <Card
        key={s.key}
        className="border cursor-pointer hover:shadow-md hover:border-emerald-300 transition-all"
        onClick={() => onCardClick?.(s.key, s.label)}
        data-testid={`stat-${s.key}`}
      >
        <CardContent className="p-3 flex items-center gap-2">
          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${s.color}`}><s.icon className="w-4 h-4" /></div>
          <div>
            <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">{s.label}</div>
            <div className="text-xl font-extrabold text-slate-800 leading-none mt-0.5">{s.val ?? 0}</div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

// ============== EVENT FORM DIALOG ==============
const CATEGORY_OPTIONS = [
  { value: 'ashara', label: 'Ashara Mubaraka', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { value: 'ramadan', label: 'Ramadan', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { value: 'other', label: 'Other', color: 'bg-slate-100 text-slate-700 border-slate-300' },
];
const categoryMeta = (cat) => CATEGORY_OPTIONS.find(c => c.value === (cat || 'other')) || CATEGORY_OPTIONS[2];

const EventDialog = ({ open, onClose, onSaved, editing, defaultCategory }) => {
  const api = useApi();
  const [form, setForm] = useState({
    name: '', description: '', event_start: '', event_end: '',
    booking_start: '', booking_end: '', status: 'draft',
    category: 'ashara', year: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        name: editing.name || '',
        description: editing.description || '',
        event_start: editing.event_start || '',
        event_end: editing.event_end || '',
        booking_start: editing.booking_start || '',
        booking_end: editing.booking_end || '',
        status: editing.status || 'draft',
        category: editing.category || 'ashara',
        year: editing.year || '',
      } : { name: '', description: '', event_start: '', event_end: '', booking_start: '', booking_end: '', status: 'draft', category: defaultCategory || 'ashara', year: '' });
    }
    // eslint-disable-next-line
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('Event name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/seat/events/${editing.id}`, form);
        toast.success('Event updated');
      } else {
        await api.post('/seat/events', form);
        toast.success('Event created');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="seat-event-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Event' : 'New Event'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Category *</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border rounded px-2 py-2 text-sm" data-testid="seat-event-category">
                {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Year (Hijri)</Label>
              <Input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="1448H" data-testid="seat-event-year" />
            </div>
          </div>
          <div>
            <Label>Event Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ramadan 1448H" required data-testid="seat-event-name" autoFocus />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Event Start</Label><Input type="datetime-local" value={form.event_start || ''} onChange={(e) => setForm({ ...form, event_start: e.target.value })} /></div>
            <div><Label className="text-xs">Event End</Label><Input type="datetime-local" value={form.event_end || ''} onChange={(e) => setForm({ ...form, event_end: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Booking Start</Label><Input type="datetime-local" value={form.booking_start || ''} onChange={(e) => setForm({ ...form, booking_start: e.target.value })} /></div>
            <div><Label className="text-xs">Booking End</Label><Input type="datetime-local" value={form.booking_end || ''} onChange={(e) => setForm({ ...form, booking_end: e.target.value })} /></div>
          </div>
          <div>
            <Label>Status</Label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border rounded px-2 py-2 text-sm" data-testid="seat-event-status">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="seat-event-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== FLOOR FORM DIALOG ==============
const FloorDialog = ({ open, onClose, onSaved, eventId, editing }) => {
  const api = useApi();
  const blank = { name: '', rows: 13, cols: 30, hof_seat_limit: 1, min_age: '', no_child_under: '', is_first_floor_like: false };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        name: editing.name || '',
        rows: editing.rows || 13,
        cols: editing.cols || 30,
        hof_seat_limit: editing.hof_seat_limit ?? 0,
        min_age: editing.min_age ?? '',
        no_child_under: editing.no_child_under ?? '',
        is_first_floor_like: !!editing.is_first_floor_like,
      } : blank);
    }
    // eslint-disable-next-line
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error('Floor name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        rows: parseInt(form.rows, 10) || 13,
        cols: parseInt(form.cols, 10) || 30,
        hof_seat_limit: parseInt(form.hof_seat_limit, 10) || 0,
        min_age: form.min_age === '' ? null : parseInt(form.min_age, 10),
        no_child_under: form.no_child_under === '' ? null : parseInt(form.no_child_under, 10),
      };
      if (editing) {
        await api.put(`/seat/floors/${editing.id}`, payload);
        toast.success('Floor updated');
      } else {
        await api.post(`/seat/events/${eventId}/floors`, payload);
        toast.success('Floor created');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="seat-floor-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Edit Floor' : 'New Floor'}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Floor Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ladies First Floor" required autoFocus data-testid="seat-floor-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Rows</Label><Input type="number" min={1} max={100} value={form.rows} onChange={(e) => setForm({ ...form, rows: e.target.value })} data-testid="seat-floor-rows" /></div>
            <div><Label className="text-xs">Columns</Label><Input type="number" min={1} max={100} value={form.cols} onChange={(e) => setForm({ ...form, cols: e.target.value })} data-testid="seat-floor-cols" /></div>
          </div>
          <div>
            <Label className="text-xs">Seat limit per HOF (0 = unlimited)</Label>
            <Input type="number" min={0} value={form.hof_seat_limit} onChange={(e) => setForm({ ...form, hof_seat_limit: e.target.value })} data-testid="seat-floor-limit" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Min Age (blank = none)</Label><Input type="number" min={0} value={form.min_age} onChange={(e) => setForm({ ...form, min_age: e.target.value })} placeholder="40" /></div>
            <div><Label className="text-xs">Block if child &lt; age</Label><Input type="number" min={0} value={form.no_child_under} onChange={(e) => setForm({ ...form, no_child_under: e.target.value })} placeholder="10" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_first_floor_like} onChange={(e) => setForm({ ...form, is_first_floor_like: e.target.checked })} data-testid="seat-floor-firstfloor" />
            Treat as First-Floor (apply child rule strictly)
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="seat-floor-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== FLOOR DESIGNER ==============
const FloorDesigner = ({ floor, onBack, onUpdate }) => {
  const api = useApi();
  const [rows, setRows] = useState(floor.rows);
  const [cols, setCols] = useState(floor.cols);
  const [selected, setSelected] = useState(new Set(
    (floor.seats || []).map((s) => `${s.row},${s.col}`)
  ));
  const [disabled, setDisabled] = useState(new Set(
    (floor.disabled_cells || []).map((c) => `${c.row},${c.col}`)
  ));
  const [mode, setMode] = useState('seat'); // 'seat' | 'disable'
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null); // 'add' | 'remove'

  const seatNumberMap = React.useMemo(() => {
    const sortedCells = Array.from(selected).map(k => {
      const [r, c] = k.split(',').map(Number);
      return { row: r, col: c };
    }).sort((a, b) => a.row - b.row || a.col - b.col);
    const m = {};
    sortedCells.forEach((c, i) => { m[`${c.row},${c.col}`] = i + 1; });
    return m;
  }, [selected]);

  const toggleCell = (r, c, forceMode) => {
    const key = `${r},${c}`;
    if (mode === 'seat') {
      setSelected(prev => {
        const next = new Set(prev);
        const has = next.has(key);
        const m = forceMode ?? (has ? 'remove' : 'add');
        if (m === 'add') {
          if (disabled.has(key)) setDisabled(prev2 => { const n2 = new Set(prev2); n2.delete(key); return n2; });
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    } else {
      setDisabled(prev => {
        const next = new Set(prev);
        const has = next.has(key);
        const m = forceMode ?? (has ? 'remove' : 'add');
        if (m === 'add') {
          if (selected.has(key)) setSelected(prev2 => { const n2 = new Set(prev2); n2.delete(key); return n2; });
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    }
  };

  const handleMouseDown = (r, c) => {
    const key = `${r},${c}`;
    const isSelected = mode === 'seat' ? selected.has(key) : disabled.has(key);
    const m = isSelected ? 'remove' : 'add';
    setDragMode(m);
    setIsDragging(true);
    toggleCell(r, c, m);
  };
  const handleMouseEnter = (r, c) => {
    if (!isDragging || !dragMode) return;
    toggleCell(r, c, dragMode);
  };
  const handleMouseUp = () => { setIsDragging(false); setDragMode(null); };

  const fillAll = () => {
    const all = new Set();
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      if (!disabled.has(`${r},${c}`)) all.add(`${r},${c}`);
    }
    setSelected(all);
  };
  const clearAll = () => { setSelected(new Set()); };

  const save = async () => {
    setSaving(true);
    try {
      const selected_cells = Array.from(selected).map(k => {
        const [r, c] = k.split(',').map(Number);
        return { row: r, col: c };
      });
      const disabled_cells = Array.from(disabled).map(k => {
        const [r, c] = k.split(',').map(Number);
        return { row: r, col: c };
      });
      const res = await api.put(`/seat/floors/${floor.id}/seats`, { selected_cells, disabled_cells });
      toast.success(res.data?.message || 'Layout saved');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // adapt grid dims if floor row/col edited (user must click "Resize")
  const handleResize = async () => {
    try {
      await api.put(`/seat/floors/${floor.id}`, { rows: parseInt(rows, 10), cols: parseInt(cols, 10) });
      toast.success('Floor resized — refreshing');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resize failed');
    }
  };

  return (
    <div className="space-y-4" onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="designer-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Floors
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold truncate">{floor.name} — Designer</h2>
          <p className="text-xs text-slate-500">
            Click or drag cells to mark seats. Auto-numbering top→bottom, left→right.
          </p>
        </div>
        <Badge className="bg-emerald-600">{selected.size} seats</Badge>
      </div>

      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border bg-white">
              <button
                onClick={() => setMode('seat')}
                className={`px-3 py-1.5 text-xs font-medium ${mode === 'seat' ? 'bg-emerald-600 text-white' : 'text-slate-700'}`}
                data-testid="mode-seat"
              >Seat</button>
              <button
                onClick={() => setMode('disable')}
                className={`px-3 py-1.5 text-xs font-medium ${mode === 'disable' ? 'bg-slate-700 text-white' : 'text-slate-700'}`}
                data-testid="mode-disable"
              >Disable</button>
            </div>
            <div className="flex items-center gap-1 ml-2">
              <Label className="text-xs">Rows</Label>
              <Input type="number" min={1} max={100} value={rows} onChange={(e) => setRows(e.target.value)} className="w-16 h-7 text-xs" />
              <Label className="text-xs">Cols</Label>
              <Input type="number" min={1} max={100} value={cols} onChange={(e) => setCols(e.target.value)} className="w-16 h-7 text-xs" />
              <Button size="sm" variant="outline" onClick={handleResize}>Resize</Button>
            </div>
            <div className="ml-auto flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={fillAll}><CheckSquare className="w-3 h-3 mr-1" /> Fill all</Button>
              <Button size="sm" variant="outline" onClick={clearAll}><Square className="w-3 h-3 mr-1" /> Clear</Button>
              <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="designer-save">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save Layout
              </Button>
            </div>
          </div>

          <div className="overflow-auto p-2 bg-slate-50 rounded border" data-testid="designer-grid">
            <div className="inline-block">
              <div className="text-center text-[10px] font-semibold text-slate-500 mb-2 select-none">
                ← QIBLA →
              </div>
              <div
                className="grid gap-0.5 select-none"
                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
              >
                {Array.from({ length: rows }).map((_, r) =>
                  Array.from({ length: cols }).map((__, c) => {
                    const key = `${r},${c}`;
                    const isSel = selected.has(key);
                    const isDis = disabled.has(key);
                    const num = seatNumberMap[key];
                    let bg = 'bg-white hover:bg-emerald-50 border-slate-200';
                    if (isSel) bg = 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-700';
                    else if (isDis) bg = 'bg-slate-400 border-slate-500 text-white';
                    return (
                      <button
                        key={key}
                        onMouseDown={() => handleMouseDown(r, c)}
                        onMouseEnter={() => handleMouseEnter(r, c)}
                        type="button"
                        className={`border rounded text-[9px] font-bold leading-none transition-colors ${bg}`}
                        style={{ width: 24, height: 24 }}
                        title={`Row ${r}, Col ${c}${num ? ` · Seat #${num}` : ''}`}
                        data-testid={`cell-${r}-${c}`}
                      >
                        {isSel ? num : ''}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
          <div className="text-xs text-slate-500 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-500" /> Seat</span>
            <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-slate-400" /> Disabled</span>
            <span className="inline-flex items-center gap-1"><div className="w-3 h-3 rounded bg-white border" /> Empty</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== FLOORS LIST FOR AN EVENT ==============
const EventFloors = ({ event, onBack, refreshStats }) => {
  const api = useApi();
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [designing, setDesigning] = useState(null);
  const [showEventDlg, setShowEventDlg] = useState(false);
  const [showApplyTpl, setShowApplyTpl] = useState(false);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/seat/events/${event.id}/floors`);
      setFloors(res.data?.floors || []);
    } catch {
      toast.error('Failed to load floors');
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  // Templates list for "Apply Template" quick action
  useEffect(() => {
    if (!event?.category) return;
    (async () => {
      try {
        const r = await api.get(`/seat/templates?category=${event.category}`);
        setTemplates(r.data?.templates || []);
      } catch {}
    })();
    // eslint-disable-next-line
  }, [event?.category]);

  const applyTpl = async (tpl) => {
    if (!window.confirm(`Create floor "${tpl.name}" (${tpl.seat_count} seats) in this event from the template?`)) return;
    try {
      const res = await api.post(`/seat/templates/${tpl.id}/apply`, { event_ids: [event.id] });
      const created = res.data?.created?.length || 0;
      const skipped = res.data?.skipped?.[0]?.reason;
      if (created) toast.success(`Floor "${tpl.name}" created from template`);
      else if (skipped) toast.warning(`Skipped: ${skipped}`);
      load();
      refreshStats?.();
      setShowApplyTpl(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Delete floor "${f.name}"?`)) return;
    try {
      await api.delete(`/seat/floors/${f.id}`);
      toast.success('Deleted');
      load();
      refreshStats?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const reloadDesigning = async () => {
    if (!designing) return;
    try {
      const res = await api.get(`/seat/floors/${designing.id}`);
      setDesigning(res.data.floor);
      load();
      refreshStats?.();
    } catch {}
  };

  if (designing) {
    return <FloorDesigner floor={designing} onBack={() => setDesigning(null)} onUpdate={reloadDesigning} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="floors-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold truncate">{event.name}</h2>
            {event.year && <Badge variant="outline" className="text-[10px] font-mono">{event.year}</Badge>}
            <Badge className={
              event.status === 'active' ? 'bg-emerald-600' :
              event.status === 'closed' ? 'bg-slate-500' : 'bg-amber-500'
            }>{event.status}</Badge>
          </div>
          <p className="text-xs text-slate-500">{event.description || 'Floors & event configuration'}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowEventDlg(true)} data-testid="edit-event-btn">
          <Pencil className="w-3 h-3 mr-1" /> Edit Event
        </Button>
        {templates.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setShowApplyTpl(true)} data-testid="apply-tpl-btn">
            <LayoutTemplate className="w-3 h-3 mr-1" /> From Template
          </Button>
        )}
        <Button onClick={() => { setEditing(null); setShowDialog(true); }} data-testid="add-floor-btn">
          <Plus className="w-4 h-4 mr-1" /> New Floor
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : floors.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          No floors yet. Click <strong>New Floor</strong> to add one.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {floors.map((f) => (
            <Card key={f.id} className="hover:shadow-md transition-shadow" data-testid={`floor-card-${f.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-blue-600" />
                      <h3 className="font-semibold truncate">{f.name}</h3>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{f.rows} × {f.cols} grid</div>
                  </div>
                  {f.is_first_floor_like && <Badge variant="outline" className="text-xs">1st Floor Rules</Badge>}
                </div>
                <div className="flex flex-wrap gap-1.5 text-xs">
                  <Badge variant="secondary">{f.seat_count} seats</Badge>
                  <Badge variant="outline">{f.booked_count} booked</Badge>
                  <Badge variant="outline">limit: {f.hof_seat_limit || '∞'}/HOF</Badge>
                  {f.min_age != null && <Badge variant="outline">age ≥ {f.min_age}</Badge>}
                  {f.no_child_under != null && <Badge variant="outline">no child &lt; {f.no_child_under}</Badge>}
                </div>
                <div className="flex gap-1 pt-1">
                  <Button size="sm" className="flex-1" onClick={() => setDesigning(f)} data-testid={`design-${f.id}`}>
                    <MapPin className="w-3 h-3 mr-1" /> Design
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(f); setShowDialog(true); }}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(f)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <FloorDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onSaved={() => { setShowDialog(false); load(); refreshStats?.(); }}
        eventId={event.id}
        editing={editing}
      />

      <EventDialog
        open={showEventDlg}
        onClose={() => setShowEventDlg(false)}
        onSaved={() => { setShowEventDlg(false); refreshStats?.(); }}
        editing={event}
      />

      <Dialog open={showApplyTpl} onOpenChange={(o) => !o && setShowApplyTpl(false)}>
        <DialogContent className="max-w-md" data-testid="quick-apply-tpl-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-indigo-600" /> Apply a Floor Template
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              Pick a template — its name, seat layout, and rules will be copied as a new floor in this event.
            </p>
            {templates.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-6">No templates available for this category.</div>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-auto">
                {templates.map(t => (
                  <button key={t.id} onClick={() => applyTpl(t)}
                    className="w-full text-left p-2.5 rounded border hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                    data-testid={`quick-tpl-${t.id}`}>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-indigo-600" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{t.name}</div>
                        <div className="text-xs text-slate-500">{t.seat_count} seats · {t.rows}×{t.cols}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplyTpl(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== EVENTS LIST ==============
const EventsList = ({ onPick, refreshStats }) => {
  const api = useApi();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/seat/events');
      setEvents(res.data?.events || []);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (e) => {
    if (!window.confirm(`Delete event "${e.name}" and ALL its floors/bookings?`)) return;
    try {
      await api.delete(`/seat/events/${e.id}`);
      toast.success('Deleted');
      load();
      refreshStats?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <h2 className="text-lg font-bold">Events</h2>
          <p className="text-xs text-slate-500">Create events such as Ramadan, Ashara, Waaz programs.</p>
        </div>
        <Button onClick={() => { setEditing(null); setShowDialog(true); }} data-testid="add-event-btn">
          <Plus className="w-4 h-4 mr-1" /> New Event
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : events.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          No events yet. Click <strong>New Event</strong> to create one.
        </CardContent></Card>
      ) : (
        <div className="space-y-6">
          {CATEGORY_OPTIONS.map(catOpt => {
            const grp = events.filter(e => (e.category || 'other') === catOpt.value);
            if (grp.length === 0) return null;
            return (
              <div key={catOpt.value} data-testid={`category-section-${catOpt.value}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${catOpt.color}`}>{catOpt.label}</span>
                  <span className="text-xs text-slate-500">{grp.length} event{grp.length === 1 ? '' : 's'}</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {grp.map((e) => (
                    <Card key={e.id} className="hover:shadow-md transition-shadow" data-testid={`event-card-${e.id}`}>
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h3 className="font-semibold truncate">{e.name}</h3>
                              {e.year && <Badge variant="outline" className="text-[10px] font-mono">{e.year}</Badge>}
                            </div>
                            <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{e.description || 'No description'}</p>
                          </div>
                          <Badge className={
                            e.status === 'active' ? 'bg-emerald-600' :
                            e.status === 'closed' ? 'bg-slate-500' : 'bg-amber-500'
                          }>{e.status}</Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs text-center">
                          <div className="bg-blue-50 rounded p-1.5">
                            <div className="font-bold text-blue-800">{e.floor_count}</div>
                            <div className="text-blue-600 text-[10px]">floors</div>
                          </div>
                          <div className="bg-indigo-50 rounded p-1.5">
                            <div className="font-bold text-indigo-800">{e.total_seats}</div>
                            <div className="text-indigo-600 text-[10px]">seats</div>
                          </div>
                          <div className="bg-emerald-50 rounded p-1.5">
                            <div className="font-bold text-emerald-800">{e.booked_count}</div>
                            <div className="text-emerald-600 text-[10px]">booked</div>
                          </div>
                        </div>
                        {e.booking_start && (
                          <div className="text-[11px] text-slate-500">
                            Booking: {new Date(e.booking_start).toLocaleString()} → {e.booking_end ? new Date(e.booking_end).toLocaleString() : '—'}
                          </div>
                        )}
                        <div className="flex gap-1 pt-1">
                          <Button size="sm" className="flex-1" onClick={() => onPick(e)} data-testid={`open-event-${e.id}`}>
                            <Building2 className="w-3 h-3 mr-1" /> Manage Floors
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditing(e); setShowDialog(true); }}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(e)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <EventDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onSaved={() => { setShowDialog(false); load(); refreshStats?.(); }}
        editing={editing}
      />
    </div>
  );
};

// ============== CATEGORY LANDING ==============
// Shows only the two master categories (Ashara, Ramadan) with their available years.
// Clicking a year scopes the entire dashboard to that single event.
const CategoryLanding = ({ allEvents, onPickEvent, onCreateYear, onOpenTemplates, onOpenTerms, onOpenSessions }) => {
  const groups = React.useMemo(() => {
    const grp = { ashara: [], ramadan: [] };
    (allEvents || []).forEach(e => {
      const c = (e.category || 'other').toLowerCase();
      if (c === 'ashara' || c === 'ramadan') grp[c].push(e);
    });
    // Sort each by year (numeric desc) then created_at desc
    const yearNum = (y) => parseInt(String(y || '').replace(/[^0-9]/g, ''), 10) || 0;
    Object.keys(grp).forEach(k => grp[k].sort((a, b) => {
      const dy = yearNum(b.year) - yearNum(a.year);
      if (dy !== 0) return dy;
      return (b.created_at || '').localeCompare(a.created_at || '');
    }));
    return grp;
  }, [allEvents]);

  const CATEGORY_TILES = [
    {
      key: 'ashara', label: 'Ashara Mubaraka',
      gradient: 'from-emerald-600 via-emerald-700 to-teal-800',
      pill: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      ring: 'ring-emerald-200',
    },
    {
      key: 'ramadan', label: 'Ramadan',
      gradient: 'from-amber-500 via-orange-600 to-rose-700',
      pill: 'bg-amber-100 text-amber-800 border-amber-300',
      ring: 'ring-amber-200',
    },
  ];

  return (
    <div className="space-y-6" data-testid="category-landing">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {CATEGORY_TILES.map(cat => {
          const list = groups[cat.key] || [];
          return (
            <Card key={cat.key} className="overflow-hidden border-0 shadow-lg" data-testid={`category-tile-${cat.key}`}>
              <div className={`bg-gradient-to-br ${cat.gradient} text-white p-6`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider opacity-80">Master Category</div>
                    <h2 className="text-2xl font-extrabold">{cat.label}</h2>
                    <p className="text-xs opacity-80 mt-0.5">{list.length} year{list.length === 1 ? '' : 's'} configured</p>
                  </div>
                  <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
                    <Calendar className="w-7 h-7" />
                  </div>
                </div>
              </div>
              <CardContent className="p-4 space-y-3 bg-white">
                {list.length === 0 ? (
                  <div className="text-center text-slate-500 text-sm py-6">
                    No years configured yet. Create the first one →
                  </div>
                ) : (
                  <div className="space-y-2">
                    {list.map(e => (
                      <button
                        key={e.id}
                        onClick={() => onPickEvent(e)}
                        className={`w-full text-left p-3 rounded-lg border hover:shadow-md hover:${cat.ring} hover:ring-2 transition-all bg-white group`}
                        data-testid={`year-${cat.key}-${e.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`px-2.5 py-1 rounded font-mono font-bold text-sm border ${cat.pill}`}>
                            {e.year || '—'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{e.name}</div>
                            <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                              <span>{e.floor_count || 0} floors</span>
                              <span>·</span>
                              <span>{e.total_seats || 0} seats</span>
                              <span>·</span>
                              <span>{e.booked_count || 0} booked</span>
                            </div>
                          </div>
                          <Badge className={
                            e.status === 'active' ? 'bg-emerald-600' :
                            e.status === 'closed' ? 'bg-slate-500' : 'bg-amber-500'
                          }>{e.status}</Badge>
                          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <Button
                  onClick={() => onCreateYear(cat.key)}
                  variant="outline"
                  className="w-full border-dashed"
                  data-testid={`add-year-${cat.key}`}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add new {cat.label} year
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="bg-indigo-50 border-indigo-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-indigo-600 text-white flex items-center justify-center">
            <LayoutTemplate className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-indigo-900">Floor Templates</div>
            <p className="text-xs text-indigo-700">Build a floor layout once per category, then reuse across every year.</p>
          </div>
          <Button onClick={onOpenTemplates} className="bg-indigo-600 hover:bg-indigo-700" data-testid="open-templates-btn">
            <LayoutTemplate className="w-4 h-4 mr-1" /> Manage Templates
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-violet-50 border-violet-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-violet-600 text-white flex items-center justify-center">
            <FileSignature className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-violet-900">Terms & Conditions</div>
            <p className="text-xs text-violet-700">Configure the booking terms that HOFs must accept before reserving seats.</p>
          </div>
          <Button onClick={onOpenTerms} className="bg-violet-600 hover:bg-violet-700" data-testid="open-terms-btn">
            <FileSignature className="w-4 h-4 mr-1" /> Manage Terms
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-emerald-50 border-emerald-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-600 text-white flex items-center justify-center">
            <Activity className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-emerald-900">HOF Live Sessions</div>
            <p className="text-xs text-emerald-700">See who is currently signed in, on which device, and force-logout stuck sessions. Only one login per HOF is allowed at a time.</p>
          </div>
          <Button onClick={onOpenSessions} className="bg-emerald-600 hover:bg-emerald-700" data-testid="open-sessions-btn">
            <Activity className="w-4 h-4 mr-1" /> View Sessions
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== PAGE ROOT ==============
export default function SeatManagementPage() {
  const navigate = useNavigate();
  const api = useApi();
  const [selectedEvent, setSelectedEvent] = useState(null);  // null = landing
  const [activeEvent, setActiveEvent] = useState(null);      // for managing floors within a year
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState('events');
  const [allEvents, setAllEvents] = useState([]);
  const [showCreateYearDlg, setShowCreateYearDlg] = useState(null); // category key or null
  const [showGlobalTemplates, setShowGlobalTemplates] = useState(false);
  const [showGlobalTerms, setShowGlobalTerms] = useState(false);
  const [showGlobalSessions, setShowGlobalSessions] = useState(false);
  const [drilldown, setDrilldown] = useState({ open: false, kind: '', label: '' });

  const loadStats = useCallback(async (eventId) => {
    try {
      const url = eventId ? `/seat/stats?event_id=${eventId}` : '/seat/stats';
      const res = await api.get(url);
      setStats(res.data || {});
    } catch {}
    // eslint-disable-next-line
  }, []);
  const loadEvents = useCallback(async () => {
    try {
      const res = await api.get('/seat/events');
      setAllEvents(res.data?.events || []);
    } catch {}
    // eslint-disable-next-line
  }, []);
  useEffect(() => { loadStats(selectedEvent?.id); loadEvents(); }, [loadStats, loadEvents, selectedEvent?.id]);

  // When user picks a year, default tab to 'events' so they land on floors mgmt.
  const handlePickEvent = (e) => {
    setSelectedEvent(e);
    setActiveEvent(null);
    setTab('events');
  };
  const handleBackToLanding = () => {
    setSelectedEvent(null);
    setActiveEvent(null);
    setShowGlobalTemplates(false);
    setShowGlobalTerms(false);
    setShowGlobalSessions(false);
  };

  // refresh the selected event in case its counters changed
  const refreshSelected = useCallback(async () => {
    if (!selectedEvent?.id) return;
    try {
      const r = await api.get(`/seat/events/${selectedEvent.id}`);
      setSelectedEvent(prev => ({ ...prev, ...(r.data?.event || {}) }));
    } catch {}
    // eslint-disable-next-line
  }, [selectedEvent?.id]);

  // Scoped event list (just the selected one) for child panels
  const scopedEvents = selectedEvent ? [selectedEvent] : allEvents;

  const categoryMetaTop = selectedEvent ? categoryMeta(selectedEvent.category) : null;

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-7xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="seat-home-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Home
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Seat Management</h1>
          {selectedEvent ? (
            <div className="text-sm text-slate-500 flex items-center gap-1.5 flex-wrap">
              <button onClick={handleBackToLanding} className="text-emerald-700 hover:underline font-medium" data-testid="breadcrumb-categories">
                Categories
              </button>
              <span className="text-slate-400">›</span>
              <span className={`text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${categoryMetaTop?.color || ''}`}>
                {categoryMetaTop?.label || selectedEvent.category}
              </span>
              <span className="text-slate-400">›</span>
              <span className="font-semibold text-slate-700">{selectedEvent.year || selectedEvent.name}</span>
            </div>
          ) : showGlobalTemplates ? (
            <div className="text-sm text-slate-500 flex items-center gap-1.5">
              <button onClick={handleBackToLanding} className="text-emerald-700 hover:underline font-medium">Categories</button>
              <span className="text-slate-400">›</span>
              <span className="font-semibold text-slate-700">Floor Templates</span>
            </div>
          ) : showGlobalTerms ? (
            <div className="text-sm text-slate-500 flex items-center gap-1.5">
              <button onClick={handleBackToLanding} className="text-emerald-700 hover:underline font-medium">Categories</button>
              <span className="text-slate-400">›</span>
              <span className="font-semibold text-slate-700">Terms & Conditions</span>
            </div>
          ) : showGlobalSessions ? (
            <div className="text-sm text-slate-500 flex items-center gap-1.5">
              <button onClick={handleBackToLanding} className="text-emerald-700 hover:underline font-medium">Categories</button>
              <span className="text-slate-400">›</span>
              <span className="font-semibold text-slate-700">HOF Live Sessions</span>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Pick a category & year to open its dashboard.</p>
          )}
        </div>
        {selectedEvent && (
          <Button variant="outline" size="sm" onClick={handleBackToLanding} data-testid="back-to-categories">
            <ArrowLeft className="w-4 h-4 mr-1" /> Categories
          </Button>
        )}
      </div>

      {selectedEvent && (
        <StatsCards
          stats={stats}
          onCardClick={(kind, label) => setDrilldown({ open: true, kind, label })}
        />
      )}

      {!selectedEvent && !showGlobalTemplates && !showGlobalTerms && !showGlobalSessions && (
        <CategoryLanding
          allEvents={allEvents}
          onPickEvent={handlePickEvent}
          onCreateYear={(catKey) => setShowCreateYearDlg(catKey)}
          onOpenTemplates={() => setShowGlobalTemplates(true)}
          onOpenTerms={() => setShowGlobalTerms(true)}
          onOpenSessions={() => setShowGlobalSessions(true)}
        />
      )}

      {!selectedEvent && showGlobalTemplates && (
        <SeatTemplatesPanel />
      )}

      {!selectedEvent && showGlobalTerms && (
        <SeatTermsPanel />
      )}

      {!selectedEvent && showGlobalSessions && (
        <SeatHofSessionsPanel />
      )}

      {selectedEvent && (
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList>
            <TabsTrigger value="events" data-testid="tab-events"><Calendar className="w-4 h-4 mr-1" /> Floors & Event</TabsTrigger>
            <TabsTrigger value="clearance" data-testid="tab-clearance"><ShieldCheck className="w-4 h-4 mr-1" /> Clearance</TabsTrigger>
            <TabsTrigger value="restrictions" data-testid="tab-restrictions"><Ban className="w-4 h-4 mr-1" /> Restrictions</TabsTrigger>
            <TabsTrigger value="allotment" data-testid="tab-allotment"><Users className="w-4 h-4 mr-1" /> Allotment</TabsTrigger>
            <TabsTrigger value="cancellations" data-testid="tab-cancellations"><XCircle className="w-4 h-4 mr-1" /> Requests</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports"><FileText className="w-4 h-4 mr-1" /> Reports</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit"><BarChart3 className="w-4 h-4 mr-1" /> Audit</TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="mt-4">
            {activeEvent ? (
              <EventFloors event={activeEvent} onBack={() => setActiveEvent(null)} refreshStats={() => { loadStats(selectedEvent.id); loadEvents(); refreshSelected(); }} />
            ) : (
              <EventFloors event={selectedEvent} onBack={handleBackToLanding} refreshStats={() => { loadStats(selectedEvent.id); loadEvents(); refreshSelected(); }} />
            )}
          </TabsContent>

          <TabsContent value="clearance" className="mt-4">
            <SeatClearancePanel onChange={() => loadStats(selectedEvent.id)} />
          </TabsContent>

          <TabsContent value="restrictions" className="mt-4">
            <SeatRestrictionsPanel
              eventId={selectedEvent.id}
              eventLabel={`${categoryMetaTop?.label || selectedEvent.category} ${selectedEvent.year || ''}`.trim()}
            />
          </TabsContent>

          <TabsContent value="allotment" className="mt-4">
            <SeatAllotmentPanel events={scopedEvents} />
          </TabsContent>

          <TabsContent value="cancellations" className="mt-4">
            <SeatCancellationPanel onChange={() => loadStats(selectedEvent.id)} eventId={selectedEvent.id} />
          </TabsContent>

          <TabsContent value="reports" className="mt-4">
            <SeatReportsPanel events={scopedEvents} />
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <SeatAuditPanel />
          </TabsContent>
        </Tabs>
      )}

      <EventDialog
        open={!!showCreateYearDlg}
        onClose={() => setShowCreateYearDlg(null)}
        onSaved={() => { setShowCreateYearDlg(null); loadEvents(); }}
        editing={null}
        defaultCategory={showCreateYearDlg}
      />

      <StatsDrilldownSheet
        open={drilldown.open}
        kind={drilldown.kind}
        label={drilldown.label}
        eventId={selectedEvent?.id}
        onClose={() => setDrilldown({ open: false, kind: '', label: '' })}
      />
    </div>
  );
}
